import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Settings.module.sass";
import Icon from "../Icon";
import { authFetch } from "../../utils/authFetch";

const Settings = ({ userType = "host" }) => {
    const [activeSection, setActiveSection] = useState("password");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [billingLoading, setBillingLoading] = useState(false);
    const [planName, setPlanName] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const email = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_email') || '' : '';
    const authToken = typeof window !== 'undefined' ?
        window.localStorage.getItem('postaway_auth_token') : null;

    // Load current plan from backend (plans table joined via subscription)
    useEffect(() => {
        if (!email) return;
        const loadPlan = async () => {
            try {
                const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4000' : '';
                let endpoint;
                if (userType === 'creator') {
                    endpoint = '/api/creator/profile';
                } else if (userType === 'host') {
                    endpoint = '/api/host/profile';
                } else if (userType === 'traveler') {
                    endpoint = '/api/traveler/profile';
                } else {
                    return;
                }
                const res = await fetch(`${base}${endpoint}?email=${encodeURIComponent(email)}`);
                const data = await res.json();
                if (res.ok && data.ok) {
                    // host: data.profile.plan_name, creator: data.profile.plan_name, traveler: may not have plan_name
                    const p = data.profile?.plan_name || "";
                    if (p) setPlanName(p);
                }
            } catch (_) {}
        };
        loadPlan();
    }, [email, userType]);

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setMessage({ text: "", type: "" });

        // Validate passwords
        if (!currentPassword || !newPassword || !confirmPassword) {
            setMessage({ text: "All fields are required", type: "error" });
            return;
        }

        if (newPassword.length < 8) {
            setMessage({ text: "New password must be at least 8 characters", type: "error" });
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage({ text: "New passwords do not match", type: "error" });
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await authFetch('/api/user/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    currentPassword,
                    newPassword
                })
            });

            const data = await response.json();

            if (response.ok && data.ok) {
                setMessage({ text: "Password changed successfully!", type: "success" });
                // Clear form
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
            } else {
                const errorMessage = data.error === 'incorrect_password' 
                    ? "Current password is incorrect"
                    : data.error === 'password_too_short'
                    ? "Password must be at least 8 characters"
                    : "Failed to change password. Please try again.";
                setMessage({ text: errorMessage, type: "error" });
            }
        } catch (error) {
            console.error('Password change error:', error);
            setMessage({ text: "An error occurred. Please try again.", type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleteLoading(true);
        try {
            const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4000' : '';
            const response = await authFetch(`${base}/api/user/delete-account`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });

            if (response.ok) {
                // Clear all local storage
                localStorage.clear();
                // Redirect to home page
                window.location.href = '/';
            } else {
                const data = await response.json();
                alert(data.error || 'Failed to delete account');
            }
        } catch (error) {
            // Don't log auth errors since authFetch handles logout
            if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
                console.error('Delete account error:', error);
                alert('An error occurred while deleting your account');
            }
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleManageBilling = async () => {
        setBillingLoading(true);
        try {
            const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4000' : '';
            const returnUrl = userType === 'traveler' 
                ? `${window.location.origin}/traveler-settings`
                : userType === 'creator'
                ? `${window.location.origin}/creator-dashboard?tab=settings`
                : `${window.location.origin}/host-dashboard?tab=settings`;
            
            const response = await fetch(`${base}/api/stripe/portal`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, userType, returnUrl })
            });

            const data = await response.json();

            if (response.ok && data.ok && data.url) {
                window.location.href = data.url;
            } else {
                const errorMessage = data.error === 'billing_portal_not_enabled'
                    ? "Billing portal is not enabled. Please contact support."
                    : data.error === 'customer_not_found_in_stripe'
                    ? "Customer not found. Please try again."
                    : data.error === 'invalid_customer_retry'
                    ? "Please try again - we're setting up your billing account."
                    : "Failed to open billing portal. Please try again.";
                alert(errorMessage);
                setBillingLoading(false);
            }
        } catch (error) {
            console.error('Billing portal error:', error);
            alert('An error occurred. Please try again.');
            setBillingLoading(false);
        }
    };

    return (
        <div className={styles.settings}>
            <div className={styles.container}>
                <h2 className={cn("h2", styles.title)}>Settings</h2>
                
                <div className={styles.content}>
                    <div className={styles.sidebar}>
                        <button
                            className={cn(styles.sidebarItem, {
                                [styles.active]: activeSection === "password"
                            })}
                            onClick={() => setActiveSection("password")}
                        >
                            <Icon name="lock" size="20" />
                            <span>Password</span>
                        </button>
                        <button
                            className={cn(styles.sidebarItem, {
                                [styles.active]: activeSection === "billing"
                            })}
                            onClick={() => setActiveSection("billing")}
                        >
                            <Icon name="credit-card" size="20" />
                            <span>Billing</span>
                        </button>
                        <button
                            className={cn(styles.sidebarItem, {
                                [styles.active]: activeSection === "notifications"
                            })}
                            onClick={() => setActiveSection("notifications")}
                        >
                            <Icon name="bell" size="20" />
                            <span>Notifications</span>
                        </button>
                        <button
                            className={cn(styles.sidebarItem, {
                                [styles.active]: activeSection === "account"
                            })}
                            onClick={() => setActiveSection("account")}
                        >
                            <Icon name="user" size="20" />
                            <span>Account</span>
                        </button>
                        {userType === 'host' && (
                            <button
                                className={cn(styles.sidebarItem, {
                                    [styles.active]: activeSection === "resources"
                                })}
                                onClick={() => setActiveSection("resources")}
                            >
                                <Icon name="book" size="20" />
                                <span>Resources</span>
                            </button>
                        )}
                        <button
                            className={cn(styles.sidebarItem, {
                                [styles.active]: activeSection === "store"
                            })}
                            onClick={() => setActiveSection("store")}
                        >
                            <Icon name="bag" size="20" />
                            <span>Store</span>
                        </button>
                    </div>

                    <div className={styles.main}>
                        {message.text && (
                            <div className={cn(styles.message, {
                                [styles.error]: message.type === "error",
                                [styles.success]: message.type === "success"
                            })}>
                                {message.text}
                            </div>
                        )}

                        {activeSection === "password" && (
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Change Password</h3>
                                <p className={styles.sectionDescription}>
                                    Update your password to keep your account secure.
                                </p>
                                
                                <form onSubmit={handlePasswordChange} className={styles.form}>
                                    <div className={styles.field}>
                                        <label className={styles.label}>Current Password</label>
                                        <div className={styles.inputWrapper}>
                                            <input
                                                type={showCurrentPassword ? "text" : "password"}
                                                value={currentPassword}
                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                className={styles.input}
                                                placeholder="Enter current password"
                                                disabled={isSubmitting}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                className={styles.eyeButton}
                                            >
                                                <Icon name="eye" size="20" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className={styles.field}>
                                        <label className={styles.label}>New Password</label>
                                        <div className={styles.inputWrapper}>
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className={styles.input}
                                                placeholder="Enter new password (min. 8 characters)"
                                                disabled={isSubmitting}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className={styles.eyeButton}
                                            >
                                                <Icon name="eye" size="20" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className={styles.field}>
                                        <label className={styles.label}>Confirm New Password</label>
                                        <div className={styles.inputWrapper}>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className={styles.input}
                                                placeholder="Confirm new password"
                                                disabled={isSubmitting}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className={styles.eyeButton}
                                            >
                                                <Icon name="eye" size="20" />
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className={cn("button", styles.submitButton)}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Updating..." : "Update Password"}
                                    </button>
                                </form>
                            </div>
                        )}

                        {activeSection === "billing" && (
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Billing & Subscription</h3>
                                <p className={styles.sectionDescription}>
                                    Manage your subscription, payment methods, and billing history.
                                </p>
                                
                                <div className={styles.billingInfo}>
                                    <div className={styles.billingCard}>
                                        <div className={styles.billingCardHeader}>
                                            <Icon name="credit-card" size="24" />
                                            <div>
                                                <h4>Current Plan</h4>
                                                <p className={styles.planType}>
                                                    {planName || (userType === 'creator' ? 'Creator Account' : userType === 'host' ? 'Host Account' : 'Traveler Account')}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className={styles.billingDetails}>
                                            <p className={styles.accountEmail}>
                                                <Icon name="email" size="16" />
                                                {email}
                                            </p>
                                        </div>

                                        <button
                                            onClick={handleManageBilling}
                                            className={cn("button", styles.billingButton)}
                                            disabled={billingLoading}
                                        >
                                            <Icon name="arrow-right" size="16" />
                                            {billingLoading ? "Loading..." : "Manage Billing"}
                                        </button>
                                    </div>

                                    <div className={styles.billingFeatures}>
                                        <h4>What you can manage:</h4>
                                        <ul className={styles.featureList}>
                                            <li>
                                                <Icon name="check" size="16" />
                                                Update payment methods
                                            </li>
                                            <li>
                                                <Icon name="check" size="16" />
                                                View billing history & invoices
                                            </li>
                                            <li>
                                                <Icon name="check" size="16" />
                                                Change or cancel subscription
                                            </li>
                                            <li>
                                                <Icon name="check" size="16" />
                                                Download tax documents
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === "notifications" && (
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Notification Preferences</h3>
                                <p className={styles.sectionDescription}>
                                    Choose how you want to receive updates and notifications.
                                </p>
                                <div className={styles.comingSoon}>
                                    <Icon name="bell" size="48" />
                                    <p>Notification settings coming soon</p>
                                </div>
                            </div>
                        )}

                        {activeSection === "account" && (
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Account Settings</h3>
                                <p className={styles.sectionDescription}>
                                    Manage your account details and preferences.
                                </p>
                                
                                <div style={{
                                    marginTop: '40px',
                                    padding: '20px',
                                    border: '1px solid #dc1d08',
                                    borderRadius: '8px',
                                    background: 'rgba(220, 29, 8, 0.05)'
                                }}>
                                    <h4 style={{
                                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#dc1d08',
                                        marginBottom: '12px'
                                    }}>Danger Zone</h4>
                                    <p style={{
                                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                        fontSize: '14px',
                                        color: '#666',
                                        marginBottom: '16px'
                                    }}>
                                        Once you delete your account, there is no going back. Please be certain.
                                    </p>
                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        style={{
                                            padding: '10px 20px',
                                            background: '#dc1d08',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    >
                                        Delete My Account
                                    </button>
                                </div>
                            </div>
                        )}

                        {userType === 'host' && activeSection === "resources" && (
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Best Practices for a Successful Collaboration</h3>
                                <div className={styles.billingFeatures}>
                                    <p className={styles.sectionDescription}>
                                        Use a contract that specifies deliverables and expectations for both hosts and creators including these items:
                                    </p>
                                    <ul className={styles.featureList}>
                                        <li>Terms of payment and/or terms of hosted stay</li>
                                        <li>Are food, beverage, activities included in the stay</li>
                                        <li>Dates of stay with check in and check out times</li>
                                        <li>Clarification on whether ads can be shot during the stay and if the property/destination requires credit in all media used going forward</li>
                                        <li>How many stories expected per day?</li>
                                        <li>How many reels expected per day?</li>
                                        <li>How many in-feed posts expected?</li>
                                        <li>What are the usage rights for images taken at the property?</li>
                                        <li>Where will the images/videos be deposited?</li>
                                        <li>Will there be a giveaway and if so, what are the terms?</li>
                                        <li>How long after the stay will content be expected to be posted?</li>
                                        <li>Is the creator allowed to bring guests?</li>
                                        <li>Is the creator allowed to bring pets?</li>
                                        <li>Who is responsible for damages during the stay?</li>
                                    </ul>
                                </div>

                                <h3 className={styles.sectionTitle} style={{ marginTop: '40px' }}>Guides & Downloads</h3>
                                <div className={styles.billingInfo}>
                                    <div className={styles.billingCard}>
                                        <div className={styles.billingCardHeader}>
                                            <Icon name="book" size="24" />
                                            <div>
                                                <h4>The Kitchen Experience</h4>
                                                <p className={styles.planType}>
                                                    Style & Guest Use Best Practices
                                                </p>
                                            </div>
                                        </div>
                                        <a
                                            href="/resources/the-kitchen-experience.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn("button", styles.billingButton)}
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <Icon name="download" size="16" />
                                            Download PDF
                                        </a>
                                    </div>

                                    <div className={styles.billingCard}>
                                        <div className={styles.billingCardHeader}>
                                            <Icon name="book" size="24" />
                                            <div>
                                                <h4>Guest Avatar Guide</h4>
                                                <p className={styles.planType}>
                                                    A guide for designing your stay for the guests you want to attract
                                                </p>
                                            </div>
                                        </div>
                                        <a
                                            href="/resources/guest-avatar-guide.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn("button", styles.billingButton)}
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <Icon name="download" size="16" />
                                            Download PDF
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === "store" && (
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Postaway Store</h3>
                                <p className={styles.sectionDescription}>
                                    Browse our curated collection of travel essentials and merchandise.
                                </p>
                                
                                <div className={styles.billingInfo}>
                                    <div className={styles.billingCard}>
                                        <div className={styles.billingCardHeader}>
                                            <Icon name="bag" size="24" />
                                            <div>
                                                <h4>Chestnut Hill Cabin Store</h4>
                                                <p className={styles.planType}>
                                                    Shop our partner collection
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className={styles.billingDetails}>
                                            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                                                Discover unique items from our featured properties and travel-inspired merchandise.
                                            </p>
                                        </div>

                                        <a
                                            href="https://shopchestnuthillcabin.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn("button", styles.billingButton)}
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <Icon name="arrow-right" size="16" />
                                            Visit Store
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                    padding: '20px'
                }} onClick={() => setShowDeleteModal(false)}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        maxWidth: '480px',
                        width: '100%',
                        padding: '32px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                    }} onClick={(e) => e.stopPropagation()}>
                        <h2 style={{
                            fontFamily: "'Sailors', serif",
                            fontSize: '24px',
                            color: '#0f084b',
                            marginBottom: '16px',
                            textAlign: 'center'
                        }}>Delete Account</h2>
                        
                        <p style={{
                            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                            fontSize: '16px',
                            color: '#666',
                            marginBottom: '24px',
                            textAlign: 'center',
                            lineHeight: '1.5'
                        }}>
                            Are you sure you want to delete your account? This action cannot be undone.
                        </p>
                        
                        <div style={{
                            background: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '6px',
                            padding: '12px',
                            marginBottom: '24px'
                        }}>
                            <p style={{
                                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                fontSize: '14px',
                                color: '#856404',
                                margin: 0
                            }}>
                                <strong>Warning:</strong> This will permanently delete:
                            </p>
                            <ul style={{
                                margin: '8px 0 0 20px',
                                padding: 0,
                                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                fontSize: '14px',
                                color: '#856404'
                            }}>
                                <li>Your profile and all personal information</li>
                                {userType === 'host' ? (
                                    <>
                                        <li>All your properties and listings</li>
                                        <li>All collaboration requests and messages</li>
                                    </>
                                ) : (
                                    <>
                                        <li>All your collaborations and messages</li>
                                    </>
                                )}
                                <li>Your subscription will be cancelled</li>
                            </ul>
                        </div>
                        
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    padding: '12px 24px',
                                    background: '#f8f9fa',
                                    color: '#666',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                disabled={deleteLoading}
                                style={{
                                    padding: '12px 24px',
                                    background: deleteLoading ? '#999' : '#dc1d08',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    cursor: deleteLoading ? 'not-allowed' : 'pointer',
                                    transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={(e) => !deleteLoading && (e.currentTarget.style.opacity = '0.9')}
                                onMouseLeave={(e) => !deleteLoading && (e.currentTarget.style.opacity = '1')}
                            >
                                {deleteLoading ? 'Deleting...' : 'Yes, Delete My Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
